import { useCallback, useEffect } from 'react';

import { datadog } from '@sg/expo-datadog-client';

import { useTelemetry } from '@order/Telemetry';

import { addAwsWafScript, getErrorMessage, initializeAwsWafNativeSdk } from '../../utils';
import { useAwsWafTokenSource } from '../useAwsWafTokenSource';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sets up AWS WAF on app start: the challenge script on web, and the native
 * token provider on native, so a token is ready before the first protected request.
 * The token source is attached to every Mixpanel event to compare the sources.
 */
export const useAwsWafScript = () => {
  const tokenSource = useAwsWafTokenSource();
  const { isInitialized, setSuperProperty } = useTelemetry();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const addWebScript = useCallback(() => {
    if (tokenSource !== 'web-script') {
      return;
    }

    addAwsWafScript();
  }, [tokenSource]);

  const initializeNativeSdk = useCallback(() => {
    if (tokenSource !== 'native-sdk') {
      return;
    }

    initializeAwsWafNativeSdk().catch(logNativeSdkInitializationError);
  }, [tokenSource]);

  // Super properties set before Mixpanel is initialized are dropped.
  const trackTokenSource = useCallback(() => {
    if (!isInitialized) {
      return;
    }

    setSuperProperty(TOKEN_SOURCE_SUPER_PROPERTY, tokenSource);
  }, [isInitialized, setSuperProperty, tokenSource]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(addWebScript, [addWebScript]);
  useEffect(initializeNativeSdk, [initializeNativeSdk]);
  useEffect(trackTokenSource, [trackTokenSource]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function logNativeSdkInitializationError(error: unknown) {
  const errorMessage = getErrorMessage(error);

  datadog.logger.error('AWS WAF | Native SDK initialization error', { error: errorMessage });
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TOKEN_SOURCE_SUPER_PROPERTY = 'awsWafTokenSource';
